import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

// GoalFilters — filter row above the IEP-goals grid.
//
// Domain / status values match the shipped schema in
// supabase/migrations/0002_pbis_data_model.sql, not the previous mock
// values. Priority is intentionally omitted — there is no priority column
// on public.behavior_goals, so a Priority filter would silently do nothing.
//
// Additional filters (student, date range, etc.) can be layered on later
// without touching the page layout because everything below is driven by
// the `filters` object.

const domainOptions = [
  { value: 'all', label: 'All areas' },
  { value: 'academic', label: 'Academic' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'social-emotional', label: 'Social / Emotional' },
  { value: 'communication', label: 'Communication' },
  { value: 'functional', label: 'Functional / Life skills' },
  { value: 'motor', label: 'Motor' },
  { value: 'transition', label: 'Transition' },
  { value: 'other', label: 'Other' },
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'mastered', label: 'Mastered' },
  { value: 'discontinued', label: 'Discontinued' },
];

const sortOptions = [
  { value: 'recent', label: 'Recently updated' },
  { value: 'progress', label: 'Progress (high → low)' },
  { value: 'dueDate', label: 'Target date (soonest first)' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

const GoalFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
  searchQuery,
  onSearchChange,
  totalGoals,
  filteredCount,
}) => {
  const hasActiveFilters =
    filters?.domain !== 'all' ||
    filters?.status !== 'all' ||
    (searchQuery?.trim() ?? '') !== '';

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="relative mb-4">
        <Icon
          name="Search"
          size={16}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          placeholder="Search goals by statement, target, or student…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Select
          label="Area"
          options={domainOptions}
          value={filters?.domain}
          onChange={(value) => onFilterChange('domain', value)}
          className="w-full"
        />
        <Select
          label="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
          className="w-full"
        />
        <Select
          label="Sort by"
          options={sortOptions}
          value={filters?.sortBy}
          onChange={(value) => onFilterChange('sortBy', value)}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          Showing {filteredCount} of {totalGoals} goals
        </span>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={14} className="mr-1" />
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
};

export default GoalFilters;
